<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserNotificationStore } from '@/stores/useUserNotificationStore'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'

import {
  BellIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  TicketIcon,
  TrophyIcon,
  InboxIcon,
  CheckIcon,
  TrashIcon,
  MegaphoneIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/vue/24/outline'

const router = useRouter()
const notificationStore = useUserNotificationStore()
const { notifications, isLoading, unreadCount } = storeToRefs(notificationStore)

const filterStatus = ref<'ALL' | 'NEW' | 'READ' | 'ARCHIVED'>('NEW')
const expandedNotificationUuid = ref<string | null>(null)

onMounted(async () => {
  await fetchNotifications()
  await notificationStore.fetchUnreadCount()
})

const fetchNotifications = async () => {
  const status = filterStatus.value === 'ALL' ? undefined : filterStatus.value
  await notificationStore.fetchNotifications({
    status,
    includeArchived: filterStatus.value === 'ARCHIVED'
  })
}

const setFilter = async (status: 'ALL' | 'NEW' | 'READ' | 'ARCHIVED') => {
  filterStatus.value = status
  await fetchNotifications()
}

const markAsRead = async (uuid: string) => {
  await notificationStore.markAsRead(uuid)
}

const markAllAsRead = async () => {
  await notificationStore.markAllAsRead()
}

const archiveNotification = async (uuid: string) => {
  await notificationStore.archiveNotification(uuid)
}

const hasEntityLink = (item: any) => {
  const { entityType } = item.notification
  return entityType === 'ORDER' || entityType === 'CAMPAIGN' || entityType === 'ORDER_DEROGATION'
}

const goToEntity = async (item: any) => {
  const { entityType, entityId } = item.notification

  if (item.status === 'NEW') {
    await markAsRead(item.notificationUuid)
  }

  switch (entityType) {
    case 'ORDER':
      router.push(`/dashboard/order/${entityId}`)
      break
    case 'CAMPAIGN':
      router.push(`/campaigns/${entityId}/details`)
      break
    case 'ORDER_DEROGATION': {
      const orderUuid = item.notification.payload?.orderUuid
      if (orderUuid) {
        router.push(`/dashboard/order/${orderUuid}`)
      } else {
        router.push('/dashboard/derogations')
      }
      break
    }
  }
}

const toggleExpand = async (item: any) => {
  if (expandedNotificationUuid.value === item.notificationUuid) {
    expandedNotificationUuid.value = null
  } else {
    expandedNotificationUuid.value = item.notificationUuid
    if (item.status === 'NEW') {
      await markAsRead(item.notificationUuid)
    }
  }
}

const getIcon = (type: string) => {
  switch (type) {
    case 'QUOTE_SIGNED':
      return CheckCircleIcon
    case 'ORDER_CREATED':
      return InboxIcon
    case 'ORDER_VALIDATED':
      return CheckCircleIcon
    case 'ORDER_CANCELED':
      return XCircleIcon
    case 'ORDER_IN_PROGRESS':
      return ArrowPathIcon
    case 'TICKET_CREATED':
      return TicketIcon
    case 'TICKET_RESOLVED':
      return TrophyIcon
    case 'ADMIN_NOTIFICATION':
      return MegaphoneIcon
    case 'CAMPAIGN_MEMBER_ADDED':
      return MegaphoneIcon
    case 'CAMPAIGN_SESSIONS_REASSIGNED':
      return ArrowPathIcon
    default:
      return BellIcon
  }
}

const formatDate = (date: string) => {
  return moment(date).format('DD/MM/YYYY [à] HH:mm')
}

const getAuthorLabel = (item: any) => {
  const author = item.notification.author
  if (!author) return null
  const name = [author.firstName, author.lastName].filter(Boolean).join(' ')
  return name || null
}
</script>

<template>
  <div class="p-6">
    <div class="flex items-center justify-between gap-4">
      <Bs2Title
        title="Mes Notifications"
        :subtitle="`Vous avez ${unreadCount} notification(s) non lue(s)`"
      />
      <div class="flex gap-2">
        <button
          v-if="unreadCount > 0"
          class="btn btn-xs btn-outline"
          @click="markAllAsRead"
        >
          Tout marquer comme lu
        </button>
      </div>
    </div>

    <div class="mt-8 card bg-base-100 shadow">
      <div class="card-body">
        <div class="flex flex-row gap-2 border-b border-gray-200 text-sm">
          <button
            class="pb-2 px-3 transition-colors"
            :class="filterStatus === 'ALL' ? 'border-b-2 border-primary font-bold text-primary' : 'text-gray-500'"
            @click="setFilter('ALL')"
          >
            Toutes
          </button>
          <button
            class="pb-2 px-3 transition-colors"
            :class="filterStatus === 'NEW' ? 'border-b-2 border-primary font-bold text-primary' : 'text-gray-500'"
            @click="setFilter('NEW')"
          >
            Nouvelles
          </button>
          <button
            class="pb-2 px-3 transition-colors"
            :class="filterStatus === 'READ' ? 'border-b-2 border-primary font-bold text-primary' : 'text-gray-500'"
            @click="setFilter('READ')"
          >
            Lues
          </button>
        </div>

        <div v-if="isLoading" class="flex justify-center py-8">
          <span class="loading loading-spinner loading-md"></span>
        </div>

        <div v-else-if="notifications.length === 0" class="flex flex-col items-center justify-center py-12">
          <InboxIcon class="w-12 h-12 text-gray-300 mb-2" />
          <p class="text-gray-500 text-sm">Aucune notification à afficher</p>
        </div>

        <div v-else class="flex flex-col gap-2 mt-4">
          <div
            v-for="item in notifications"
            :key="item.notificationUuid"
            class="bg-base-100 rounded-lg shadow-sm hover:shadow transition-shadow group border border-gray-50"
            :class="{ 'border-l-4 border-l-primary': item.status === 'NEW' }"
          >
            <div class="flex flex-row items-center gap-3 p-3">
              <div class="bg-gray-50 p-2 rounded-lg shrink-0">
                <component :is="getIcon(item.notification.type)" class="w-5 h-5 text-gray-500" />
              </div>

              <div
                class="flex-1 min-w-0 cursor-pointer"
                @click="toggleExpand(item)"
              >
                <div class="flex items-center gap-2 mb-0.5">
                  <h3 class="font-bold text-sm truncate" :class="{ 'text-primary': item.status === 'NEW' }">
                    {{ item.notification.title }}
                  </h3>
                  <span v-if="getAuthorLabel(item)" class="text-[11px] text-gray-400 shrink-0">
                    par {{ getAuthorLabel(item) }}
                  </span>
                </div>
                <p
                  v-if="expandedNotificationUuid !== item.notificationUuid"
                  class="text-xs text-gray-600 line-clamp-1"
                >
                  {{ item.notification.description }}
                </p>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <button
                  v-if="hasEntityLink(item)"
                  class="btn btn-ghost btn-xs px-1 text-info hover:bg-info/10"
                  title="Voir le détail"
                  @click.stop="goToEntity(item)"
                >
                  <ArrowTopRightOnSquareIcon class="w-4 h-4" />
                </button>

                <div class="flex flex-col items-end gap-1">
                  <span class="text-[10px] text-gray-400 whitespace-nowrap">
                    {{ formatDate(item.notification.createdAt) }}
                  </span>
                  <div class="flex flex-row gap-1 opacity-0 group-hover:opacity-100 transition-opacity h-6">
                    <button
                      v-if="item.status === 'NEW'"
                      class="btn btn-ghost btn-xs px-1 text-primary hover:bg-primary/10"
                      title="Marquer comme lu"
                      @click.stop="markAsRead(item.notificationUuid)"
                    >
                      <CheckIcon class="w-4 h-4" />
                    </button>
                    <button
                      class="btn btn-ghost btn-xs px-1 text-error hover:bg-error/10"
                      title="Archiver"
                      @click.stop="archiveNotification(item.notificationUuid)"
                    >
                      <TrashIcon class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="expandedNotificationUuid === item.notificationUuid"
              class="px-5 pb-4 pt-0 border-t border-gray-100"
            >
              <div
                class="text-sm text-gray-700 prose prose-sm max-w-none mt-3 whitespace-pre-wrap"
                v-html="item.notification.description"
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
